import { useEffect, useState, useCallback } from 'react';
import { api } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { Clock, CheckCircle, Trash, ArrowRight, Plus } from '@phosphor-icons/react';
import OpeningBalanceImportDialog from '../components/OpeningBalanceImportDialog';
import SearchBar from '../components/SearchBar';

const fmt = (n) => '₹' + Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 2 });

export default function PendingOpeningBalancesPage() {
  const [data, setData] = useState({ rows: [], totals: {} });
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [search, setSearch] = useState('');
  const [agencies, setAgencies] = useState([]);
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [migrateDialogId, setMigrateDialogId] = useState(null);
  const [migrateAgencyId, setMigrateAgencyId] = useState('');
  const [importOpen, setImportOpen] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [pob, ag] = await Promise.all([
        api.get(`/pending-opening-balances?status=${statusFilter}`),
        api.get('/agencies'),
      ]);
      setData(pob.data || { rows: [], totals: {} });
      setAgencies(ag.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Load failed');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const startEdit = (row) => {
    setEditId(row.id);
    setEditForm({
      agency_name: row.agency_name,
      mobile: row.mobile,
      oil_company: row.oil_company,
      kind: row.kind,
      amount: row.amount,
      note: row.note,
    });
  };

  const saveEdit = async (id) => {
    try {
      await api.put(`/pending-opening-balances/${id}`, editForm);
      toast.success('Updated');
      setEditId(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Update failed');
    }
  };

  const deleteRow = async (id) => {
    if (!window.confirm('Pending entry cancel kar dein?')) return;
    try {
      await api.delete(`/pending-opening-balances/${id}`);
      toast.success('Cancelled');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    }
  };

  const manualMigrate = async () => {
    if (!migrateAgencyId) { toast.error('Agency select karo'); return; }
    try {
      await api.post(`/pending-opening-balances/${migrateDialogId}/migrate`, { agency_id: migrateAgencyId });
      toast.success('Migrated to ledger');
      setMigrateDialogId(null);
      setMigrateAgencyId('');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Migrate failed');
    }
  };

  const filteredRows = (data.rows || []).filter((r) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (r.agency_name || '').toLowerCase().includes(q)
      || (r.mobile || '').includes(q)
      || (r.oil_company || '').toLowerCase().includes(q)
      || (r.note || '').toLowerCase().includes(q);
  });

  return (
    <div className="space-y-4 p-4 sm:p-6" data-testid="pending-opening-balances-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
        <div>
          <h1 className="text-2xl font-bold text-[#002FA7] flex items-center gap-2">
            <Clock size={26} weight="duotone" /> Pending Opening Balances
          </h1>
          <p className="text-xs text-gray-600 mt-1">
            Un-onboarded agencies ke Khatabook/Tally balances. Jab agency system me create hogi, auto-link ho jayegi.
          </p>
        </div>
        <Button onClick={() => setImportOpen(true)} className="gap-1 bg-amber-600 hover:bg-amber-700" size="sm" data-testid="open-import-dlg-btn">
          <Plus size={16} /> Add Pending Entries
        </Button>
      </div>

      {/* Totals */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="bg-amber-50 border border-amber-200 rounded p-3" data-testid="stat-pending-count">
          <div className="text-[10px] uppercase text-amber-700 font-semibold">Pending Entries</div>
          <div className="text-2xl font-bold text-amber-900">{data.totals?.pending_count || 0}</div>
        </div>
        <div className="bg-emerald-50 border border-emerald-200 rounded p-3" data-testid="stat-migrated-count">
          <div className="text-[10px] uppercase text-emerald-700 font-semibold">Migrated</div>
          <div className="text-2xl font-bold text-emerald-900">{data.totals?.migrated_count || 0}</div>
        </div>
        <div className="bg-red-50 border border-red-200 rounded p-3">
          <div className="text-[10px] uppercase text-red-700 font-semibold">Total Pending Amt</div>
          <div className="text-xl font-bold text-red-900">{fmt(data.totals?.total_pending_amount)}</div>
        </div>
        <div className="bg-violet-50 border border-violet-200 rounded p-3">
          <div className="text-[10px] uppercase text-violet-700 font-semibold">Total Advance Amt</div>
          <div className="text-xl font-bold text-violet-900">{fmt(data.totals?.total_advance_amount)}</div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 items-center">
        {['pending', 'migrated', 'cancelled', 'all'].map((s) => (
          <Button key={s} variant={statusFilter === s ? 'default' : 'outline'} size="sm" onClick={() => setStatusFilter(s)} data-testid={`filter-${s}`}>
            {s.charAt(0).toUpperCase() + s.slice(1)}
          </Button>
        ))}
        <div className="ml-auto">
          <SearchBar
            value={search}
            onChange={setSearch}
            placeholder="Search name / mobile / OMC"
            matchCount={filteredRows.length}
            totalCount={(data.rows || []).length}
            noun="entries"
            testId="pending-ob-search"
            inputClassName="pl-8 pr-7 h-8 text-xs"
          />
        </div>
      </div>

      {/* Table */}
      <div className="border rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="pending-table">
            <thead className="bg-gray-50 text-[10px] uppercase">
              <tr>
                <th className="px-3 py-2 text-left">Agency Name</th>
                <th className="px-3 py-2 text-left">Mobile</th>
                <th className="px-3 py-2 text-left">OMC</th>
                <th className="px-3 py-2 text-left">Kind</th>
                <th className="px-3 py-2 text-right">Amount</th>
                <th className="px-3 py-2 text-left">Note</th>
                <th className="px-3 py-2 text-left">Status</th>
                <th className="px-3 py-2 text-left">Suggestions / Migrated to</th>
                <th className="px-3 py-2 text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={9} className="text-center py-6 text-gray-500">Loading…</td></tr>}
              {!loading && filteredRows.length === 0 && (
                <tr><td colSpan={9} className="text-center py-6 text-gray-500">Koi entry nahi — "Add Pending Entries" se shuru karein</td></tr>
              )}
              {filteredRows.map((r) => {
                const isEditing = editId === r.id;
                return (
                  <tr key={r.id} className="border-t hover:bg-gray-50 align-top">
                    <td className="px-3 py-2 font-medium">
                      {isEditing ? <Input value={editForm.agency_name} onChange={(e) => setEditForm({ ...editForm, agency_name: e.target.value })} className="h-7 text-xs" /> : r.agency_name}
                    </td>
                    <td className="px-3 py-2">
                      {isEditing ? <Input value={editForm.mobile || ''} onChange={(e) => setEditForm({ ...editForm, mobile: e.target.value })} className="h-7 text-xs w-28" /> : (r.mobile || '—')}
                    </td>
                    <td className="px-3 py-2">{r.oil_company || '—'}</td>
                    <td className="px-3 py-2">
                      {isEditing ? (
                        <select value={editForm.kind} onChange={(e) => setEditForm({ ...editForm, kind: e.target.value })} className="text-xs border rounded px-1 py-0.5">
                          <option value="pending">Pending</option>
                          <option value="advance">Advance</option>
                        </select>
                      ) : (
                        <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${r.kind === 'pending' ? 'bg-red-100 text-red-700' : 'bg-emerald-100 text-emerald-700'}`}>
                          {r.kind?.toUpperCase()}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right font-mono">
                      {isEditing ? <Input type="number" value={editForm.amount} onChange={(e) => setEditForm({ ...editForm, amount: e.target.value })} className="h-7 text-xs text-right w-24" /> : fmt(r.amount)}
                    </td>
                    <td className="px-3 py-2 text-gray-600">
                      {isEditing ? <Input value={editForm.note || ''} onChange={(e) => setEditForm({ ...editForm, note: e.target.value })} className="h-7 text-xs" /> : (r.note || '—')}
                    </td>
                    <td className="px-3 py-2">
                      <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                        r.status === 'migrated' ? 'bg-emerald-100 text-emerald-700' :
                        r.status === 'cancelled' ? 'bg-gray-200 text-gray-600' : 'bg-amber-100 text-amber-700'
                      }`}>
                        {r.status?.toUpperCase()}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      {r.status === 'migrated' && (
                        <div className="text-[10px]">
                          <CheckCircle size={11} weight="fill" className="text-emerald-600 inline mr-1" />
                          {r.migrated_to_agency_name}
                        </div>
                      )}
                      {r.status === 'pending' && (r.suggestions || []).length > 0 && (
                        <div className="space-y-0.5">
                          {(r.suggestions || []).slice(0, 2).map((s) => (
                            <div key={s.id} className="text-[10px] text-violet-700">
                              ≈ {s.name} <span className="text-gray-400">({s.score}%)</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-center">
                      {isEditing ? (
                        <div className="flex gap-1 justify-center">
                          <Button size="sm" onClick={() => saveEdit(r.id)} className="h-6 text-[10px] px-2 bg-emerald-600">Save</Button>
                          <Button size="sm" variant="outline" onClick={() => setEditId(null)} className="h-6 text-[10px] px-2">X</Button>
                        </div>
                      ) : r.status === 'pending' ? (
                        <div className="flex gap-1 justify-center">
                          <Button size="sm" variant="outline" onClick={() => startEdit(r)} className="h-6 text-[10px] px-2" data-testid={`edit-${r.id}`}>Edit</Button>
                          <Button size="sm" variant="outline" onClick={() => { setMigrateDialogId(r.id); setMigrateAgencyId(''); }} className="h-6 text-[10px] px-2 text-violet-700 border-violet-300" data-testid={`migrate-${r.id}`}>
                            <ArrowRight size={11} /> Link
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => deleteRow(r.id)} className="h-6 text-[10px] px-2 text-red-600 border-red-300" data-testid={`delete-${r.id}`}>
                            <Trash size={11} />
                          </Button>
                        </div>
                      ) : <span className="text-gray-400 text-[10px]">—</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Manual migrate modal */}
      {migrateDialogId && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setMigrateDialogId(null)}>
          <div className="bg-white rounded-lg p-5 max-w-md w-full" onClick={(e) => e.stopPropagation()} data-testid="migrate-dialog">
            <h3 className="text-lg font-bold mb-2">Manually Link to Agency</h3>
            <p className="text-xs text-gray-600 mb-3">Existing agency choose karo — uska distributor_ledger me opening_balance entry ban jayegi.</p>
            <select value={migrateAgencyId} onChange={(e) => setMigrateAgencyId(e.target.value)} className="w-full border rounded px-2 py-2 text-sm" data-testid="migrate-agency-select">
              <option value="">-- select agency --</option>
              {agencies.map((a) => <option key={a.id} value={a.id}>{a.name} ({a.oil_company || '—'})</option>)}
            </select>
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" onClick={() => setMigrateDialogId(null)}>Cancel</Button>
              <Button onClick={manualMigrate} disabled={!migrateAgencyId} className="bg-violet-600 hover:bg-violet-700" data-testid="migrate-confirm-btn">Migrate</Button>
            </div>
          </div>
        </div>
      )}

      <OpeningBalanceImportDialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onImported={() => { fetchData(); }}
      />
    </div>
  );
}
